import React, { useMemo } from 'react'
import { Button, Icon, Li, Link, Typography, Ul } from '@vp/swan'
import { useMyAccountResourcesContent } from '@vp/my-account-pages-dropdown'
import type { BookendsHeaderLink } from '@vp/my-account-pages-dropdown'
import { useI18n } from '../../../../context/TranslationContext'
import { useAuth } from '../../../../hooks/useAuth'
import type { HeaderLink } from '../../../../types/site-header'
import { getHeaderTracker } from '../../../../tracking'
import { DEFAULT_MY_FAVORITES_URL } from '../../../../constants/my-account'

type TopMobileMenuProps = {
  helpLink: HeaderLink
  myProjectsLink: HeaderLink
  openAccountPanel: (name: string) => void
  isRedesignedSignInButtonEnabled: boolean
}

const handleLinkClick = (analyticsId: string, href?: string) => {
  getHeaderTracker().trackLink({
    navigationDetailPath: [analyticsId],
    href,
  })
}

const LinkItem = ({ href, text, analyticsId }: { href: string | undefined; text: string | undefined; analyticsId?: string }) => (
  <Li>
    <Link skin="unstyled" fontSkin="body-standard" href={href} py={4} width="full-width" onClick={() => handleLinkClick(analyticsId || text || '', href)}>
      {text}
    </Link>
  </Li>
)

const TopMobileMenu: React.FC<TopMobileMenuProps> = ({ helpLink, myProjectsLink, openAccountPanel, isRedesignedSignInButtonEnabled }) => {
  const { translate } = useI18n()
  const { content } = useMyAccountResourcesContent()
  const { isSignedIn, signOut, signIn } = useAuth()

  const myFavoritesLink = useMemo(() => content?.signedIn.bookendsHeaderLinks.find((link) => link.id === 'myFavorites'), [content])
  const accountText = content?.signedIn.header.sideBarTitle ?? translate('account')
  const signOutText = content?.signedIn.footer.signOutButtonText ?? translate('signOut')
  const signInText = content?.signedOut.header.signInButtonText ?? translate('signIn')

  const handleAccountClick = () => {
    openAccountPanel(accountText)
  }

  if (isRedesignedSignInButtonEnabled)
    return (
      <RedesignedTopMobileMenu
        {...{
          isSignedIn,
          signIn,
          signInText,
          handleAccountClick,
          accountText,
          helpLink,
          myProjectsLink,
          signOut,
          signOutText,
          myFavoritesLink,
        }}
      />
    )

  return (
    <Ul skin="minimal" style={{ gap: 0 }}>
      <LinkItem href={helpLink.url} text={helpLink.text} analyticsId={helpLink.analyticsId || 'help'} />
      <LinkItem href={myProjectsLink.url} text={myProjectsLink.text} analyticsId={myProjectsLink.analyticsId || 'myProjects'} />
      <LinkItem href={myFavoritesLink?.url ?? DEFAULT_MY_FAVORITES_URL} text={myFavoritesLink?.text ?? translate('myFavorites')} analyticsId="myFavorites" />

      {isSignedIn ? (
        <>
          <Li>
            <Button skin="unstyled" display="flex" width="full-width" style={{ justifyContent: 'space-between' }} py={4} onClick={handleAccountClick}>
              <Typography fontSkin="body-standard">{accountText}</Typography>
              <Icon size="24p" iconType="caretRight" aria-label={translate('menu.goToAccount')} />
            </Button>
          </Li>
          <Li>
            <Button skin="unstyled" onClick={signOut} py={4} width="full-width">
              {signOutText}
            </Button>
          </Li>
        </>
      ) : (
        <Li>
          <Button skin="unstyled" width="full-width" onClick={signIn} fontSkin="body-standard" py={4}>
            {signInText}
          </Button>
        </Li>
      )}
    </Ul>
  )
}

type RedesignedTopMobileMenuProps = {
  isSignedIn: boolean
  signIn: () => void
  signInText: string
  handleAccountClick: () => void
  accountText: string
  helpLink: HeaderLink
  myProjectsLink: HeaderLink
  signOut: (e: React.MouseEvent<HTMLElement>) => void
  signOutText: string
  myFavoritesLink?: BookendsHeaderLink
}

const RedesignedTopMobileMenu: React.FC<RedesignedTopMobileMenuProps> = ({
  isSignedIn,
  signIn,
  signInText,
  handleAccountClick,
  accountText,
  helpLink,
  myProjectsLink,
  signOut,
  signOutText,
  myFavoritesLink,
}) => {
  const { translate } = useI18n()

  return (
    <Ul skin="minimal" style={{ gap: 0 }}>
      {!isSignedIn && (
        <Li>
          <Button skin="primary" width="full-width" onClick={signIn} fontSkin="body-standard" iconPosition="left">
            <Icon iconType="account" size="standard" aria-hidden="true" />
            {signInText}
          </Button>
        </Li>
      )}
      {isSignedIn && (
        <Li>
          <Button skin="unstyled" display="flex" width="full-width" style={{ justifyContent: 'space-between' }} py={4} onClick={handleAccountClick}>
            <Typography fontSkin="body-standard">{accountText}</Typography>
            <Icon size="24p" iconType="caretRight" aria-label={translate('menu.goToAccount')} />
          </Button>
        </Li>
      )}
      <LinkItem href={myFavoritesLink?.url ?? DEFAULT_MY_FAVORITES_URL} text={myFavoritesLink?.text ?? translate('myFavorites')} analyticsId="myFavorites" />
      {isSignedIn && <LinkItem href={myProjectsLink.url} text={myProjectsLink.text} analyticsId={myProjectsLink.analyticsId || 'myProjects'} />}
      <LinkItem href={helpLink.url} text={helpLink.text} analyticsId={helpLink.analyticsId || 'help'} />
      {isSignedIn && (
        <Li>
          <Button skin="unstyled" onClick={signOut} py={4} width="full-width">
            {signOutText}
          </Button>
        </Li>
      )}
    </Ul>
  )
}

export default TopMobileMenu
